import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import { getUrlPrefix, Language } from '../utils/languageUtils';
import { useImpersonation } from '../contexts/ImpersonationContext';

const AuthCallback: React.FC = () => {
  const { error, isLoading } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasError, setHasError] = useState(false);
  const [isProcessingImpersonation, setIsProcessingImpersonation] = useState(false);
  const { t, language } = useI18n();
  const { startImpersonation } = useImpersonation();

  // Handle impersonation token
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const impersonateToken = searchParams.get('impersonate');

    if (impersonateToken) {
      setIsProcessingImpersonation(true);
      // This is an impersonation request
      startImpersonation(impersonateToken).then((success) => {
        const langPrefix = getUrlPrefix(language as Language);
        if (success) {
          // Redirect to account page as the impersonated user
          navigate(`/${langPrefix}/account`, { replace: true });
        } else {
          // Impersonation failed, redirect to home
          setHasError(true);
          setTimeout(() => {
            navigate(`/${langPrefix}/`, { replace: true });
          }, 3000);
        }
      });
      return;
    }
  }, [location.search, startImpersonation, navigate, language]);

  useEffect(() => {
    // Check if we have the required callback parameters (for Auth0)
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const impersonateToken = searchParams.get('impersonate');

    // Skip Auth0 checks if this is an impersonation request
    if (impersonateToken) {
      return;
    }

    if (!code || !state) {
      console.error(t.errors.missingCallbackParams);
      setHasError(true);
      // Redirect to home page after a delay
      setTimeout(() => {
        const savedLanguage = localStorage.getItem('fireandice_language') || 'et';
        const langPrefix = getUrlPrefix((savedLanguage as Language) || 'et');
        navigate(`/${langPrefix}/`, { replace: true });
      }, 3000);
      return;
    }

    // The Auth0Provider automatically handles the callback when it detects
    // the code and state parameters in the URL. We just need to check for errors.
    if (error) {
      console.error(t.console.errors.authCallbackError, error);
      setHasError(true);
      
      // Log additional debugging information
      console.error(t.console.errors.authErrorDetails, {
        error: error.message,
        errorDescription: (error as any).error_description,
        domain: process.env.REACT_APP_AUTH0_DOMAIN,
        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
        redirectUri: `${window.location.origin}/callback`
      });
      
      // Redirect to home page after a delay
      setTimeout(() => {
        const savedLanguage = localStorage.getItem('fireandice_language') || 'et';
        const langPrefix = getUrlPrefix((savedLanguage as Language) || 'et');
        navigate(`/${langPrefix}/`, { replace: true });
      }, 3000);
    }
  }, [error, navigate, location.search, t.errors.missingCallbackParams, t.console.errors.authCallbackError, t.console.errors.authErrorDetails]);

  // Show error message if there's an error
  if (hasError || error) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t.errors.authenticationError}</h2>
          <p className="text-gray-600 mb-4">
            {t.errors.authenticationErrorMessage}
          </p>
          {error && (
            <p className="text-sm text-gray-500 mb-4">
              {t.errors.authError.replace('{{message}}', error.message)}
            </p>
          )}
          <div className="animate-pulse text-sm text-gray-500">{t.errors.redirecting}</div>
        </div>
      </div>
    );
  }

  // Show loading while Auth0Provider processes the callback or impersonation is in progress
  return (
    <div className="flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">
          {isProcessingImpersonation
            ? 'Setting up admin view...'
            : (isLoading ? t.errors.authenticating : t.errors.loading)}
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;